import React, { useState, useEffect, useRef } from 'react';
import { Card, CardBody, CardHeader, Col, Row, Button, Input, Label, Spinner, Badge } from 'reactstrap';
import { ArrowLeft, Printer, Download, Filter, DollarSign, FileText, MapPin, Truck, Calendar } from 'react-feather';
import { useNavigate } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { apiPost } from '../../../Utils';
import Breadcrumbs from '../../../CommonElements/Breadcrumbs';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import DataTable from 'react-data-table-component';
import { useAuth } from '../../../Context/AuthContext';

const ExpenseReport = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState<any[]>([]);
    const [totalAmount, setTotalAmount] = useState(0);
    const [branches, setBranches] = useState<any[]>([]);
    const [vehicles, setVehicles] = useState<any[]>([]);
    const { hasPermission } = useAuth();
    // Filters
    const [filters, setFilters] = useState({
        branch_id: '',
        vehicle_id: '',
        from_date: '',
        to_date: '',
    });

    const [startDate, setStartDate] = useState<Date | null>(null);
    const [endDate, setEndDate] = useState<Date | null>(null);

    // Pagination & Sort State
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(10);
    const [totalRows, setTotalRows] = useState(0);
    const [sortColumn, setSortColumn] = useState('date');
    const [sortDirection, setSortDirection] = useState('desc');

    const componentRef = useRef(null);

    // Fetch Initial Data
    useEffect(() => {
        apiPost("branches/list").then((res: any) => setBranches(Array.isArray(res) ? res : [])).catch(err => console.error(err));
    }, []);

    // Fetch Vehicles when Branch changes
    useEffect(() => {
        const payload: any = {};
        if (filters.branch_id) payload.branch_id = filters.branch_id;

        // Reset selected vehicle when branch changes
        setFilters(prev => ({ ...prev, vehicle_id: '' }));

        apiPost("vehicles/list", payload)
            .then((res: any) => setVehicles(Array.isArray(res) ? res : []))
            .catch(err => {
                console.error(err);
                setVehicles([]);
            });
    }, [filters.branch_id]);

    // Fetch Report
    // Helper to format date in local YYYY-MM-DD
    const formatDate = (date: Date) => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        return `${year}-${month}-${day}`;
    };

    // Fetch Report
    const fetchReport = (page: number, size: number, sortCol: string, sortDir: string) => {
        setLoading(true);
        const payload = {
            ...filters,
            from_date: startDate ? formatDate(startDate) : '',
            to_date: endDate ? formatDate(endDate) : '',
            page: page,
            per_page: size,
            sort_by: sortCol,
            sort_order: sortDir
        };

        apiPost("reports/expense", payload)
            .then((res: any) => {
                setReportData(res.expenses || []);
                setTotalAmount(res.total_amount || 0);
                setTotalRows(res.total || 0);
                setLoading(false);
            })
            .catch(err => {
                console.error(err);
                setLoading(false);
            });
    };

    // Trigger fetch on state changes
    useEffect(() => {
        fetchReport(currentPage, perPage, sortColumn, sortDirection);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage, perPage, sortColumn, sortDirection, filters.branch_id, filters.vehicle_id, startDate, endDate]);



    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    const handlePerRowsChange = (newPerPage: number, page: number) => {
        setPerPage(newPerPage);
        setCurrentPage(page);
    };

    const handleSort = (column: any, sortDirection: string) => {
        const field = column.sortField || 'date';
        setSortColumn(field);
        setSortDirection(sortDirection);
    };

    const handleDownload = async () => {
        setLoading(true);
        try {
            const payload = {
                ...filters,
                from_date: startDate ? formatDate(startDate) : '',
                to_date: endDate ? formatDate(endDate) : '',
                page: 1,
                per_page: totalRows > 0 ? totalRows : 10000, // Fetch all rows
                sort_by: sortColumn,
                sort_order: sortDirection
            };

            const res: any = await apiPost("reports/expense", payload);
            const allData = res.expenses || []; // Assuming 'expenses' is the key for the data array
            // Recalculate total for PDF footer if needed, or use the one from response if available. 
            // Usually totals are same as screen but strictly speaking we should sum the 'allData'
            const pdfTotal = allData.reduce((sum: number, item: any) => sum + Number(item.amount || 0), 0);

            const doc = new jsPDF() as any;
            doc.setFontSize(18);
            doc.text("Expense Report", 14, 20);
            doc.setFontSize(10);
            doc.text(`Generated: ${new Date().toLocaleDateString()}`, 14, 26);
            if (startDate && endDate) {
                doc.text(`Period: ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`, 14, 31);
            }

            const tableRows = allData.map((item: any) => [
                item.date ? String(item.date).split('T')[0] : '',
                item.vehicle,
                item.branch,
                item.type,
                item.category || '-',
                item.description,
                Number(item.amount).toFixed(2)
            ]);

            autoTable(doc, {
                startY: 40,
                head: [['Date', 'Vehicle', 'Branch', 'Type', 'Category', 'Description', 'Amount']],
                body: tableRows,
                theme: 'grid',
                headStyles: { fillColor: [41, 128, 185] },
                foot: [['', '', '', '', '', 'Total', Number(pdfTotal).toFixed(2)]],
                footStyles: { fillColor: [240, 240, 240], textColor: 0, fontStyle: 'bold', halign: 'right' },
                columnStyles: { 6: { halign: 'right' } },
                showFoot: 'lastPage'
            });

            doc.save('Expense_Report.pdf');
        } catch (error) {
            console.error("Error downloading expense report", error);
        } finally {
            setLoading(false);
        }
    };

    const handlePrint = useReactToPrint({
        content: () => componentRef.current,
    });

    const columns = [
        {
            name: 'Sl.No',
            cell: (row: any, index: number) => (currentPage - 1) * perPage + index + 1,
            width: '80px',
        },
        {
            name: 'Date',
            selector: (row: any) => row.date,
            sortable: true,
            sortField: 'date',
            cell: (row: any) => <span>{row.date ? new Date(row.date).toLocaleDateString('en-GB') : ''}</span>
        },
        {
            name: 'Branch',
            selector: (row: any) => row.branch,
            sortable: true,
            sortField: 'branch',
        },
        {
            name: 'Vehicle',
            selector: (row: any) => row.vehicle,
            sortable: true,
            sortField: 'vehicle',
            cell: (row: any) => <span className="fw-bold">{row.vehicle}</span>
        },
        {
            name: 'Type',
            selector: (row: any) => row.type,
            sortable: true,
            sortField: 'type',
            cell: (row: any) => (
                <Badge color={
                    row.type === 'Fuel Expense' ? 'danger' :
                        row.type === 'Insurance' ? 'success' :
                            row.type === 'RC Renewal' ? 'info' :
                                row.type === 'Maintenance' ? 'warning' :
                                    row.type === 'LPO' ? 'primary' : 'secondary'
                }>{row.type}</Badge>
            )
        },
        {
            name: 'Amount',
            selector: (row: any) => row.amount,
            sortable: true,
            sortField: 'amount',
            cell: (row: any) => <span className="fw-bold">{Number(row.amount).toFixed(2)}</span>
        },
    ];

    const customStyles = {
        headRow: {
            style: {
                backgroundColor: '#e0f2f1',
                color: '#00695c',
                minHeight: '50px',
                borderBottom: '1px solid #e0e0e0',
            },
        },
        headCells: {
            style: {
                fontSize: '15px',
                fontWeight: '600',
                paddingLeft: '16px',
                paddingRight: '16px',
            },
        },
        rows: {
            style: {
                minHeight: '50px',
                '&:not(:last-of-type)': {
                    borderBottomStyle: 'solid',
                    borderBottomWidth: '1px',
                    borderBottomColor: '#e0e0e0',
                },
            },
        },
    };

    return (
        <div className="page-body">
            <Breadcrumbs parent="Reports" title="Expense Report" pageTitle="Expense Report" />
            <div className="container-fluid">
                <Row className="d-print-none">
                    <Col md={4}>
                        <Card className="h-80 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #fff5f5 0%, #fff 100%)', borderLeft: '4px solid #dc3545' }}>
                            <CardBody className="py-3 d-flex align-items-center justify-content-between">
                                <div>
                                    <h6 className="text-muted text-uppercase small fw-bold mb-2">Total Expenditure</h6>
                                    <h4 className="text-danger mb-0 fw-bold">{Number(totalAmount).toFixed(2)}</h4>
                                </div>
                                <div className="p-3 bg-white rounded-circle shadow-sm">
                                    <DollarSign size={24} className="text-danger" />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4}>
                        <Card className="h-80 border-0 shadow-sm" style={{ background: 'linear-gradient(135deg, #e3f2fd 0%, #fff 100%)', borderLeft: '4px solid #0d6efd' }}>
                            <CardBody className="p-3 d-flex align-items-center justify-content-between">
                                <div>
                                    <h6 className="text-muted text-uppercase small fw-bold mb-2">Total Transactions</h6>
                                    <h4 className="text-primary mb-0 fw-bold">{totalRows}</h4>
                                </div>
                                <div className="p-3 bg-white rounded-circle shadow-sm">
                                    <FileText size={24} className="text-primary" />
                                </div>
                            </CardBody>
                        </Card>
                    </Col>
                    <Col md={4} className="bg-transparent border-0 d-flex justify-content-end align-items-center px-0  flex-wrap gap-2">
                        {hasPermission('expense-report.download') && (
                            <Button
                                color="success"
                                className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm"
                                onClick={handleDownload}
                            >
                                <Download size={16} /> <span className="fw-bold">Download</span>
                            </Button>
                        )}
                        {hasPermission('expense-report.print') && (
                            <Button
                                color="primary"
                                className="px-4 py-2 rounded-pill d-flex align-items-center gap-2 shadow-sm"
                                onClick={handlePrint}
                            >
                                <Printer size={16} /> <span className="fw-bold">Print</span>
                            </Button>
                        )}
                    </Col>
                </Row>

                <Card className="border-0 shadow-sm mb-4 bg-light">
                    <CardBody className="p-4">

                        <Row className="g-3">
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1">
                                    <MapPin size={14} /> Select Branch
                                </Label>
                                <Input
                                    type="select"
                                    value={filters.branch_id}
                                    onChange={e => {
                                        setFilters({ ...filters, branch_id: e.target.value });
                                        setCurrentPage(1);
                                    }}
                                    bsSize="sm"
                                    className="border-0 bg-white shadow-sm"
                                    style={{ borderRadius: '8px', padding: '8px 12px' }}
                                >
                                    <option value="">All Branches</option>
                                    {branches.map(b => <option key={b.id} value={b.id}>{b.branch_name}</option>)}
                                </Input>
                            </Col>
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1">
                                    <Truck size={14} /> Filter Vehicle
                                </Label>
                                <Input
                                    type="select"
                                    value={filters.vehicle_id}
                                    onChange={e => {
                                        setFilters({ ...filters, vehicle_id: e.target.value });
                                        setCurrentPage(1);
                                    }}
                                    bsSize="sm"
                                    className="border-0 bg-white shadow-sm"
                                    style={{ borderRadius: '8px', padding: '8px 12px' }}
                                >
                                    <option value="">All Vehicles</option>
                                    {vehicles.map(v => <option key={v.id} value={v.id}>{v.registration_number}</option>)}
                                </Input>
                            </Col>
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1">
                                    <Calendar size={14} /> From Date
                                </Label>
                                <div className="shadow-sm bg-white rounded-3 overflow-hidden">
                                    <DatePicker
                                        className="form-control border-0 bg-transparent"
                                        selected={startDate}
                                        onChange={(date) => {
                                            setStartDate(date);
                                            setCurrentPage(1);
                                        }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="Start Date"
                                        wrapperClassName="w-100"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </Col>
                            <Col md={3}>
                                <Label className="small text-secondary fw-semibold mb-1 d-flex align-items-center gap-1">
                                    <Calendar size={14} /> To Date
                                </Label>
                                <div className="shadow-sm bg-white rounded-3 overflow-hidden">
                                    <DatePicker
                                        className="form-control border-0 bg-transparent"
                                        selected={endDate}
                                        onChange={(date) => {
                                            setEndDate(date);
                                            setCurrentPage(1);
                                        }}
                                        dateFormat="dd/MM/yyyy"
                                        placeholderText="End Date"
                                        wrapperClassName="w-100"
                                        showMonthDropdown
                                        showYearDropdown
                                        dropdownMode="select"
                                    />
                                </div>
                            </Col>
                        </Row>
                    </CardBody>
                </Card>

                {loading ? <div className="d-flex justify-content-center align-items-center w-100" style={{ minHeight: '50vh' }}>
                    <Spinner color="primary" />
                </div> : (
                    <div ref={componentRef}>
                        {/* Print Header (Only visible when printing) */}
                        <div className="d-none d-print-block text-center mb-4">
                            <h2>Expense Report</h2>
                            <p className="text-muted">Generated on {new Date().toLocaleDateString()}</p>
                        </div>



                        <Card className="border-0 shadow-sm">
                            <CardBody className="p-0">
                                <DataTable
                                    columns={columns}
                                    data={reportData}
                                    pagination
                                    paginationServer
                                    paginationTotalRows={totalRows}
                                    paginationDefaultPage={currentPage}
                                    onChangePage={handlePageChange}
                                    onChangeRowsPerPage={handlePerRowsChange}
                                    sortServer
                                    onSort={handleSort}
                                    highlightOnHover
                                    striped
                                    persistTableHead
                                    responsive
                                    progressPending={loading}
                                    progressComponent={<div className="p-5 text-center"><Spinner color="primary" /></div>}
                                    noDataComponent={<div className="p-4 text-center text-muted">No expense records found matching criteria</div>}
                                    customStyles={customStyles}
                                />
                                {reportData.length > 0 && (
                                    <div className="bg-light p-3 text-end fw-bold border-top">
                                        Grand Total: <span className="text-primary fs-6 ms-2">{Number(totalAmount).toFixed(2)}</span>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    </div>
                )}
            </div>
            <style>
                {`
                    @media print {
                        .d-print-none { display: none !important; }
                        .d-print-block { display: block !important; }
                        .page-body { padding: 0; margin: 0; }
                        .card { border: none !important; box-shadow: none !important; }
                        .rdt_Pagination { display: none !important; }
                    }
                `}
            </style>
        </div>
    );
};

export default ExpenseReport;
